import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { useServices } from '@/context/ServicesProvider';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TituloSeccion from '@/components/elements/TituloSeccion';
import Boton from '@/components/elements/Boton';
import ArticleIcon from '@mui/icons-material/Article';
import PublishIcon from '@mui/icons-material/Publish';
import { primary, white } from '@/constants/Colors';
import { obtenerExtensionArchivo } from '@/util/Exporter';
import { FileTypeExporter, ImportarLibrosResponse } from '@/objects/Exporter';
import { AxiosResponse } from 'axios';
import Texto from '@/components/elements/Texto';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import { mostrarAlerta } from '@/context/UtilsContext';
import ExporterResumen from './ExporterResumen';
import TituloDescripcion from '@/components/elements/TituloDescripcion';
import { useNotificationContext } from '@/context/NotificationContext';
import { esPremium } from '@/util/StringsUtil';
import { abrirAlertPremium } from '@/util/Comun';

function Exporter() {
    const { t } = useTranslation();
    const { usersQueries } = useServices();
    const { userData } = useNotificationContext();

    const [archivoSeleccionado, setArchivoSeleccionado] = useState<File | undefined>();
    const [isButtonDisabled, isSetButtonDisabled] = useState<boolean>(true);

    const getFilenameFromHeaders = (headers: Record<string, any>, fallback: string): string => {
        const contentDisposition = headers?.['content-disposition'] as string | undefined;
        if (!contentDisposition) {
            return fallback;
        }

        const fileNameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (fileNameStarMatch?.[1]) {
            return decodeURIComponent(fileNameStarMatch[1]).replace(/"/g, '');
        }

        const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
        if (fileNameMatch?.[1]) {
            return fileNameMatch[1];
        }

        return fallback;
    };

    const downloadBlob = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const isProbablyBase64 = (value: string): boolean => {
        const normalized = value.replace(/\s/g, '');
        if (!normalized || normalized.length % 4 !== 0) {
            return false;
        }

        return /^[A-Za-z0-9+/]+={0,2}$/.test(normalized);
    };

    const base64ToBlob = (base64: string, mimeType: string): Blob => {
        const normalized = base64.replace(/\s/g, '');
        const binary = atob(normalized);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) {
            bytes[i] = binary.charCodeAt(i);
        }
        return new Blob([bytes], { type: mimeType });
    };

    function exportarCatalogo(plantilla: number = 0) {
        usersQueries?.exportarLibrosUsuario(plantilla, FileTypeExporter.EXTENSION_EXCEL).then(async (response) => {
            const contentType = (response.headers?.['content-type'] || '').toLowerCase();
            const filename = getFilenameFromHeaders(response.headers, 'catalogo.xlsx');

            if (contentType.includes('application/json') || contentType.includes('text/plain')) {
                const errorMessage = await response.data.text();
                throw new Error(errorMessage || 'La API no devolvio un archivo valido.');
            }

            let blob = new Blob([response.data], { type: response.data.type || contentType || 'application/octet-stream' });

            // Algunos backends envian XLSX como cadena base64 (por ejemplo, empezando por UEsDB).
            const textPayload = await response.data.text();
            const trimmedPayload = textPayload.trim();
            if (trimmedPayload.startsWith('UEsDB') && isProbablyBase64(trimmedPayload)) {
                blob = base64ToBlob(trimmedPayload, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            }

            downloadBlob(blob, filename);
        }).catch((error) => {
            console.error('Error al exportar catalogo:', error);
            mostrarAlerta({
                title: t('common:opciones.error'),
                content: t('menu:secciones.importar_exportar.error_al_importar'),
                showCancelButton: false,
            });
        });
    }

    //Para importar el catálogo, primero se selecciona el archivo y luego se hace click en el botón de importar
    const hadleImport = () => {
        if (archivoSeleccionado) {
            const extension = obtenerExtensionArchivo(archivoSeleccionado.name);
            if (extension !== FileTypeExporter.EXTENSION_CSV_TYPE && extension !== FileTypeExporter.EXTENSION_EXCEL) {
                mostrarAlerta({
                    title: t('common:opciones.error'),
                    content: t("menu:secciones.importar_exportar.error_formato_csv"),
                    showCancelButton: false,
                });
                return;
            }
            handlePostCSV(extension);
        }
    };

    const handlePostCSV = (format: FileTypeExporter) => {
        if (!esPremium(userData?.premium)) {
            abrirAlertPremium(t);
            return;
        }
        if (archivoSeleccionado) {
            usersQueries!.importarLibrosUsuario(archivoSeleccionado, format).then((response: AxiosResponse<ImportarLibrosResponse>) => {
                if (response.status === 200) {
                    mostrarAlerta({
                        title: t('menu:secciones.importar_exportar.se_ha_terminado_de_importar'),
                        content: <ExporterResumen importarLibrosResumen={response.data} />,
                        showCancelButton: false,
                        acceptText: t('common:opciones.aceptar'),
                    });
                    // Limpiar el input
                    setArchivoSeleccionado(undefined);
                    isSetButtonDisabled(true);
                } else {
                    mostrarAlerta({
                        title: t('common:opciones.error'),
                        content: t("menu:secciones.importar_exportar.error_al_importar"),
                        showCancelButton: false,
                    });
                }
            }
            ).catch((error: any) => {
                console.error('Error al importar archivo:', error);
            });
        }
    }

    //para seleccionar el archivo CSV
    const handleSeleccionarArchivo = () => {
        if (!esPremium(userData?.premium)) {
            abrirAlertPremium(t);
            return;
        }
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.csv, .xls, .xlsx';
        input.click();
        input.onchange = () => {
            if (input.files) {
                setArchivoSeleccionado(input.files[0]);
                isSetButtonDisabled(false);
            }
        };
    };

    return (
        <section style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <TituloSeccion texto={t("menu:secciones.importar_exportar.gestion")} />
            <TituloDescripcion >{t("menu:secciones.importar_exportar.instrucciones_exportar")}</TituloDescripcion>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', width: "100%" }}>
                <div style={{ marginLeft: "10px", marginTop: "10px" }}>
                    <Boton color={primary} colorTexto={white} startIcon={<FileDownloadIcon />} texto={t("menu:secciones.importar_exportar.exportar_catalogo_csv")} onClick={() => { exportarCatalogo() }} />
                </div>
                <div style={{ marginLeft: "10px", marginTop: "10px" }}>
                    <Boton color={primary} colorTexto={white} startIcon={<ArticleIcon />} texto={t("menu:secciones.importar_exportar.descargar_plantilla_csv")} onClick={() => { exportarCatalogo(1) }} />
                </div>
            </div>
            <TituloDescripcion >{t("menu:secciones.importar_exportar.instrucciones_importar")}</TituloDescripcion>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', width: "100%" }}>
                <div style={{ marginLeft: "10px", marginTop: "10px" }}>
                    <Boton color={primary} colorTexto={white} startIcon={<FindInPageIcon />} texto={t("menu:secciones.importar_exportar.seleccionar_archivo")} onClick={() => { handleSeleccionarArchivo() }} />
                </div>
                {archivoSeleccionado && <Texto style={{ fontWeight: "800" }} >{String(archivoSeleccionado?.name)}</Texto>}
                <div style={{ marginLeft: "10px", marginTop: "10px" }}>
                    <Boton color={primary} colorTexto={white} startIcon={<PublishIcon />} texto={t("menu:secciones.importar_exportar.importar_catalogo_csv")} onClick={() => { hadleImport() }} disabled={isButtonDisabled} />
                </div>
            </div>
        </section>
    );
}

export default Exporter;